import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { shallowEqual, useSelector } from 'react-redux'
import { Collapse, IconButton, Link, List, ListItem, ListItemText, Stack, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import MoreVertIcon from '@mui/icons-material/MoreVert'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { get } from 'utils/lodash'
import T from 'T'
import { combineAddressInfo, isAddressObjectEmpty } from 'utils/helper'
import { CONTACT_METHODS, supportsPhoneExtension } from 'settings/constants/contacts'
import { canUpdateCustomerManager } from 'data/permissions/permissionsSelectors'

import HHPhoneField from 'components/form-fields/v5/HHPhoneField'
import ContactStatusAvatar from 'components/common/avatar/ContactStatusAvatar'
import ContactMethodIcon from 'components/customer-details/content/contacts/ContactMethodIcon'
import BeneficialOwnerOverrideAvatar from 'components/customer-details/content/beneficial_owner_contacts/BeneficialOwnerOverrideAvatar'
import PropertyOwnerAvatar from 'components/customer-details/content/beneficial_owner_contacts/PropertyOwnerAvatar'

const { EMAIL } = CONTACT_METHODS

const ContactView = ({ contact = {}, showActionMenu = false, onActionClick, onRowClick, bulkExpansion = null }) => {
  const [open, setOpen] = useState(true)

  const bulkVersion = bulkExpansion?.version ?? 0
  const bulkOpen = bulkExpansion?.open ?? true
  useEffect(() => {
    if (bulkVersion > 0) {
      setOpen(Boolean(bulkOpen))
    }
  }, [bulkVersion, bulkOpen])
  const updateCustomerManager = useSelector(canUpdateCustomerManager, shallowEqual)
  const contactName = `${get(contact, 'firstName', '')} ${get(contact, 'lastName', '')}`.trim() || '- - '
  const title = get(contact, 'relationship', '')
  const contactInfos = get(contact, 'contactInfos', [])
  const isDeactivated = get(contact, 'deactivated')
  const hasContactMethods = contactInfos.length > 0
  const beneficialOwnerOverride = get(contact, 'beneficialOwnerOverride', false)
  const propertyOwner = get(contact, 'propertyOwner', false)
  const firstMethodType = get(contactInfos, '[0].method')
  const firstMethodValue = get(contactInfos, '[0].data.value')
  const firstMethodPhoneExtension = get(contactInfos, '[0].data.phoneExtension', '')
  const isPropertyOwner = get(contact, 'propertyOwner', false)
  const addressObj = get(contact, 'address', null)
  const addressStr = addressObj && !isAddressObjectEmpty(addressObj) ? combineAddressInfo(addressObj) : null

  const isRowClickable = Boolean(onRowClick) && updateCustomerManager

  const handleToggle = event => {
    event.stopPropagation()
    setOpen(!open)
  }

  const handleActionClick = event => {
    event.stopPropagation()
    onActionClick(event)
  }

  const handleRowClick = () => {
    if (isRowClickable) onRowClick()
  }

  const handleRowKeyDown = event => {
    // Only handle the row's own keystrokes — inner buttons (chevron, kebab) must
    // continue to handle their own Enter/Space without double-firing edit.
    if (!isRowClickable || event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onRowClick()
    }
  }

  // Single-line preview of the first method shown under the contact name while
  // the row is collapsed. Email → raw value, anything else → formatted phone
  // (with extension only when the method type supports it). Hoisted so the JSX
  // below stays declarative.
  const showFirstMethodPreview = hasContactMethods && !open
  const firstMethodExtension = supportsPhoneExtension(firstMethodType) ? firstMethodPhoneExtension : ''
  const firstMethodPreview =
    showFirstMethodPreview && firstMethodType === EMAIL
      ? firstMethodValue
      : showFirstMethodPreview && <HHPhoneField displayType="text" value={firstMethodValue} phoneExtension={firstMethodExtension} />

  return (
    <>
      <ListItem
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        role={isRowClickable ? 'button' : undefined}
        tabIndex={isRowClickable ? 0 : undefined}
        sx={isRowClickable ? { cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } } : undefined}
      >
        <IconButton size="small" disabled={!hasContactMethods} onClick={handleToggle}>
          {open ? <ExpandMore /> : <ChevronRight />}
        </IconButton>

        <ListItemText
          sx={{ display: 'grid' }}
          primary={contactName}
          secondary={
            <Stack component="span" direction="column">
              {title && (
                <Typography variant="caption" color="textSecondary" component="span">
                  {title}
                </Typography>
              )}
              {isPropertyOwner && (
                <Typography color="textSecondary" component="span" fontWeight={700}>
                  {T.BENEFICIAL_OWNER}
                </Typography>
              )}
              {addressStr && (
                <Typography color="textSecondary" component="span" noWrap>
                  {addressStr}
                </Typography>
              )}
              {showFirstMethodPreview && (
                <Typography color="textSecondary" component="span" noWrap>
                  {firstMethodPreview}
                </Typography>
              )}
            </Stack>
          }
        />

        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="end">
          {propertyOwner && (
            <Tooltip arrow title={T.BENEFICIAL_OWNER}>
              <span>
                <PropertyOwnerAvatar isActive={propertyOwner} />
              </span>
            </Tooltip>
          )}
          {beneficialOwnerOverride && (
            <Tooltip arrow title={T.BILLING_ADDRESS_OVERRIDE}>
              <span>
                <BeneficialOwnerOverrideAvatar isActive={beneficialOwnerOverride} />
              </span>
            </Tooltip>
          )}
          {isDeactivated && (
            <Tooltip arrow title={T.DEACTIVATED_ACCOUNT}>
              <span>
                <ContactStatusAvatar isActive={!isDeactivated} />
              </span>
            </Tooltip>
          )}
          {showActionMenu && updateCustomerManager && (
            <IconButton size="small" onClick={handleActionClick}>
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding onClick={event => event.stopPropagation()}>
          {contactInfos.map(method => {
            const methodType = get(method, 'method')
            const methodValue = get(method, 'data.value')
            const methodPhoneExtension = get(method, 'data.phoneExtension', '')

            // Build the clickable contact-method link (email → mailto, anything
            // else → tel with optional DTMF extension). Extensions only apply
            // to Phone/Mobile (not Fax), so we gate the tel-suffix and the
            // HHPhoneField extension prop on `supportsPhoneExtension`. Email gets
            // ellipsis overflow so long addresses don't break the row width.
            const isEmail = methodType === EMAIL
            const extension = supportsPhoneExtension(methodType) ? methodPhoneExtension : ''
            const methodHref = isEmail ? `mailto:${methodValue}` : `tel:${methodValue}${extension ? `,,${extension}` : ''}`
            const methodLinkContent = isEmail ? (
              methodValue
            ) : (
              <HHPhoneField displayType="text" value={methodValue} phoneExtension={extension} />
            )
            const methodLink = (
              <Link
                href={methodHref}
                color="text.primary"
                underline="none"
                overflow={isEmail ? 'hidden' : undefined}
                textOverflow={isEmail ? 'ellipsis' : undefined}
              >
                {methodLinkContent}
              </Link>
            )

            return (
              <ListItem
                disablePadding
                sx={{ pt: 0, pl: 6, pr: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Grid container wrap="nowrap" display="flex" alignItems="center" spacing={0.5}>
                  <Grid display="flex">
                    <ContactMethodIcon type={methodType} />
                  </Grid>
                  <Grid size="grow">
                    <Typography noWrap display="grid">
                      {methodLink}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
            )
          })}
        </List>
      </Collapse>
    </>
  )
}

ContactView.propTypes = {
  contact: PropTypes.object,
  showActionMenu: PropTypes.bool,
  onActionClick: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  bulkExpansion: PropTypes.shape({
    open: PropTypes.bool,
    version: PropTypes.number,
  }),
}

export default ContactView
